import React, { useEffect, useRef, useState } from 'react';
import { Send, Lock, ChevronDown } from 'lucide-react';
import type { ChatMessage, Participant } from '../types/meeting.types.ts';

interface ChatPanelProps {
  messages: ChatMessage[];
  mySocketId: string;
  participants: Participant[];
  onSendMessage: (text: string, scope: 'public' | 'private', toSocketId?: string) => void;
  chatEncryptionEnabled?: boolean;
  /** Rastgele E2E oda anahtarı aktif (genel mesajlar tam uçtan uca) */
  e2eRoomActive?: boolean;
  /** ECDH açık anahtarı alınan (uçtan uca hazır) katılımcı socket kimlikleri */
  e2eReadyPeers?: string[];
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  mySocketId,
  participants,
  onSendMessage,
  chatEncryptionEnabled = false,
  e2eRoomActive = false,
  e2eReadyPeers = [],
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [recipient, setRecipient] = useState<'public' | string>('public');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const remoteParticipants = participants.filter(
    (p) => !p.isCurrentUser && p.id !== 'local'
  );

  // Seçili özel alıcı toplantıdan ayrıldıysa "Herkese"ye dön
  useEffect(() => {
    if (recipient !== 'public' && !remoteParticipants.some((p) => p.id === recipient)) {
      setRecipient('public');
    }
  }, [recipient, remoteParticipants]);

  const visibleMessages = messages.filter((msg) => {
    if (msg.scope === 'public') return true;
    return msg.senderId === mySocketId || msg.toSocketId === mySocketId;
  });

  // Yeni mesajda en alta kaydır
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [visibleMessages.length]);

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    if (recipient === 'public') {
      onSendMessage(newMessage, 'public');
    } else {
      onSendMessage(newMessage, 'private', recipient);
    }
    setNewMessage('');
    inputRef.current?.focus();
  };

  const formatTime = (timestamp: number) =>
    new Date(timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

  const recipientName =
    recipient === 'public'
      ? 'Herkese'
      : remoteParticipants.find((p) => p.id === recipient)?.name || 'Katılımcı';

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="hidden md:block p-4 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-white font-semibold">Sohbet</h3>
          {chatEncryptionEnabled && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full"
              title={
                e2eRoomActive
                  ? 'Tüm mesajlar uçtan uca AES-256-GCM ile şifrelenir; anahtarlar ECDH ile değiştirilir, sunucu dahil kimse okuyamaz'
                  : 'Sohbet mesajları AES-256-GCM ile şifrelenir'
              }
            >
              <Lock className="w-3 h-3" />
              {e2eRoomActive ? 'AES-256 E2E' : 'AES-256 Şifreli'}
            </span>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        {visibleMessages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-8">
            Henüz mesaj yok. Sohbete başlayın!
          </div>
        ) : (
          visibleMessages.map((msg, index) => {
            const isMe = msg.senderId === mySocketId;
            const isPrivate = msg.scope === 'private';
            const privateTargetName = isPrivate
              ? participants.find((p) => p.id === msg.toSocketId)?.name
              : null;

            // Zoom tarzı gruplama: aynı gönderenin ardışık mesajlarında başlık tekrarlanmaz
            const prev = visibleMessages[index - 1];
            const grouped =
              !!prev &&
              prev.senderId === msg.senderId &&
              prev.scope === msg.scope &&
              msg.timestamp - prev.timestamp < 2 * 60 * 1000;

            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} ${grouped ? 'mt-0.5' : 'mt-3'}`}
              >
                {!grouped && (
                  <div className={`flex items-baseline gap-2 mb-1 px-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-xs font-semibold ${isMe ? 'text-purple-300' : 'text-purple-400'}`}>
                      {isMe ? 'Siz' : msg.senderName}
                    </span>
                    {isPrivate && (
                      <span className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 px-1.5 py-px rounded-full">
                        {isMe ? `Özel → ${privateTargetName || 'Katılımcı'}` : 'Size özel'}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-500">{formatTime(msg.timestamp)}</span>
                  </div>
                )}
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm text-white break-words ${
                    isMe
                      ? 'bg-purple-600 rounded-2xl rounded-tr-md'
                      : 'bg-white/10 rounded-2xl rounded-tl-md'
                  } ${isPrivate ? 'ring-1 ring-amber-400/40' : ''}`}
                  title={formatTime(msg.timestamp)}
                >
                  {msg.text}
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={sendMessage} className="flex-shrink-0 p-3 sm:p-4 border-t border-white/10 space-y-2">
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <span className="flex-shrink-0">Kime:</span>
          <div className="relative">
            <select
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className={`appearance-none pl-2 pr-6 py-1 rounded-lg text-xs font-medium focus:outline-none cursor-pointer border ${
                recipient === 'public'
                  ? 'bg-white/5 border-white/10 text-white'
                  : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
              }`}
              aria-label="Mesaj alıcısı"
            >
              <option value="public" className="bg-slate-800 text-white">Herkese</option>
              {remoteParticipants.map((p) => (
                <option key={p.id} value={p.id} className="bg-slate-800 text-white">
                  {p.name} (Özel)
                </option>
              ))}
            </select>
            <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
          </div>
          {recipient !== 'public' && (
            <span className="inline-flex items-center gap-1 text-amber-300/80 text-[10px]">
              {e2eReadyPeers.includes(recipient) && (
                <span
                  className="inline-flex items-center gap-0.5 text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-px rounded-full"
                  title="Bu mesaj uçtan uca şifrelenir; sunucu dahil kimse okuyamaz"
                >
                  <Lock className="w-2.5 h-2.5" />
                  E2E
                </span>
              )}
              Bu mesajı sadece {recipientName} görür
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={recipient === 'public' ? 'Herkese mesaj yazın...' : `${recipientName} kişisine özel mesaj...`}
            className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 text-sm min-h-[44px]"
            aria-label="Mesaj"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center bg-purple-600 hover:bg-purple-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition"
            aria-label="Gönder"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatPanel;
